import { useState } from "react";
import { ArrowRight, ExternalLink, X } from "lucide-react";
import { Anchor } from "~/components/Anchor";
import { ImageLoader } from "~/components/ImageLoader";
import { Modal } from "~/components/Modal";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { addUtmTrackingToUrl } from "~/utils/addUtmTrackingToUrl";
import { validateEmail } from "~/utils/validators/validateEmail";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { track } from "~/modules/analytics/track";
import { useUser } from "~/modules/user/hooks/useUser";

// // // //

function getWebsiteDomain(website: string) {
    try {
        return new URL(website).hostname.replace(/^www\./, "");
    } catch {
        return null;
    }
}

/**
 * HowToBuyModal
 * A modal that displays the how to buy information for an asset.
 */
export function HowToBuyModal(props: {
    assetID: number;
    assetName: string;
    assetIconUrl?: string | null;
    platformName?: string | null;
    issuerName?: string | null;
    website: string;
    show: boolean;
    onHide: () => void;
}) {
    const {
        assetID,
        assetName,
        assetIconUrl,
        platformName,
        issuerName,
        website,
        show,
        onHide,
    } = props;
    const { user } = useUser();
    const [email, setEmail] = useState<string | null>(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [requestComplete, setRequestComplete] = useState(false);
    const profileEmail = user.email.trim();
    const resolvedEmail = email ?? profileEmail;
    const emailIsValid = validateEmail(resolvedEmail);
    const websiteDomain = getWebsiteDomain(website);

    function handleHide() {
        setEmail(null);
        setHasSubmitted(false);
        setRequestComplete(false);
        onHide();
    }

    return (
        <Modal show={show} size="lg" allowScroll onHide={handleHide}>
            <div className="relative mx-auto rounded-lg border bg-card p-5 shadow-xl sm:p-6">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="absolute top-4 right-4 text-muted-foreground"
                    onClick={handleHide}
                >
                    <X className="size-4 shrink-0" aria-hidden="true" />
                    <span className="sr-only">Close</span>
                </Button>

                <div className="pb-5 sm:pb-6">
                    <h2
                        id="modal-headline"
                        className="pr-8 text-xl font-medium tracking-tight text-balance sm:text-2xl"
                    >
                        How to buy {assetName}
                    </h2>
                </div>

                <div className="@container">
                    <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @2xl:gap-4">
                        <section className="flex min-w-0 flex-col gap-5 rounded-lg border border-black/10 p-4 sm:p-5">
                            <div className="flex flex-1 flex-col gap-3">
                                <div className="grid min-h-6 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2.5">
                                    <ImageLoader
                                        src={
                                            assetIconUrl ?? "/rwa-logo-coin.svg"
                                        }
                                        alt={`${assetName} logo`}
                                        fallbackSrc="/rwa-logo-coin.svg"
                                        className="size-5 shrink-0 rounded-full"
                                    />
                                    <h3 className="text-lg font-medium tracking-tight text-balance text-foreground sm:text-base">
                                        Buy directly through
                                        {platformName
                                            ? ` ${platformName}`
                                            : " the issuer"}
                                    </h3>
                                </div>
                                <p className="text-base text-pretty text-secondary-foreground sm:text-sm/6">
                                    Review eligibility, offering details, and
                                    purchase instructions directly from
                                    {platformName || issuerName
                                        ? ` ${platformName ?? issuerName}`
                                        : " the issuer"}
                                    .
                                </p>
                            </div>

                            <Button
                                asChild
                                size="sm"
                                variant="outline"
                                className="h-10 w-full justify-between font-normal sm:h-9"
                            >
                                <Anchor
                                    href={addUtmTrackingToUrl(website)}
                                    openInNewTab
                                    analyticsLabel={
                                        AssetUserEvents.AssetHeaderVisitProduct
                                    }
                                    analyticsEventProperties={{
                                        asset_id: assetID,
                                        asset_name: assetName,
                                        source: "how_to_buy_modal",
                                    }}
                                >
                                    Visit {websiteDomain ?? "product website"}
                                    <ExternalLink
                                        className="size-4 shrink-0"
                                        aria-hidden="true"
                                    />
                                </Anchor>
                            </Button>
                        </section>

                        <section className="flex min-w-0 flex-col gap-5 rounded-lg border border-black/10 p-4 sm:p-5">
                            <div className="flex flex-1 flex-col gap-3">
                                <div className="grid min-h-6 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2.5">
                                    <ImageLoader
                                        src="/rwa-logo-coin.svg"
                                        alt=""
                                        className="size-5 shrink-0"
                                    />
                                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                        <h3 className="text-lg font-medium tracking-tight text-balance text-foreground sm:text-base">
                                            Onboard through RWA.xyz
                                        </h3>
                                        <div className="rounded-full bg-blue-50 px-2 py-0.5 text-sm font-medium whitespace-nowrap text-blue-700 ring-1 ring-blue-600/20 sm:text-xs">
                                            Beta
                                        </div>
                                    </div>
                                </div>
                                <p className="text-base text-pretty text-secondary-foreground sm:text-sm/6">
                                    Complete KYC/KYB once and access a growing
                                    network of regulated tokenized funds.
                                </p>
                            </div>

                            <div className="min-h-[7.75rem] sm:min-h-[7.125rem]">
                                {requestComplete ? (
                                    <div
                                        className="rounded-md bg-muted/50 p-3 text-base text-secondary-foreground ring-1 ring-black/10 sm:text-sm/6"
                                        role="status"
                                    >
                                        You&apos;re on the waitlist. We&apos;ll
                                        contact you when RWA.xyz Access becomes
                                        available.
                                    </div>
                                ) : (
                                    <form
                                        className="flex flex-col gap-3"
                                        onSubmit={(event) => {
                                            event.preventDefault();
                                            setHasSubmitted(true);

                                            if (!emailIsValid) return;

                                            track(
                                                AssetUserEvents.AssetHeaderSubmitAccessRequest,
                                                {
                                                    asset_id: assetID,
                                                    asset_name: assetName,
                                                    email: resolvedEmail.trim(),
                                                },
                                            );
                                            setRequestComplete(true);
                                        }}
                                    >
                                        <div className="flex flex-col gap-1.5">
                                            <label
                                                htmlFor={`how-to-buy-email-${assetID}`}
                                                className="text-base font-medium text-foreground sm:text-sm"
                                            >
                                                Email
                                            </label>
                                            <Input
                                                id={`how-to-buy-email-${assetID}`}
                                                name="email"
                                                type="email"
                                                value={resolvedEmail}
                                                autoComplete="email"
                                                placeholder="you@company.com"
                                                aria-invalid={
                                                    hasSubmitted &&
                                                    !emailIsValid
                                                }
                                                aria-describedby={
                                                    hasSubmitted &&
                                                    !emailIsValid
                                                        ? `how-to-buy-email-error-${assetID}`
                                                        : undefined
                                                }
                                                onChange={(event) =>
                                                    setEmail(event.target.value)
                                                }
                                            />
                                            {hasSubmitted && !emailIsValid && (
                                                <p
                                                    id={`how-to-buy-email-error-${assetID}`}
                                                    className="text-base text-red-600 sm:text-sm"
                                                >
                                                    Enter a valid email address.
                                                </p>
                                            )}
                                        </div>

                                        <Button
                                            type="submit"
                                            size="sm"
                                            variant="primary"
                                            className="h-10 w-full font-normal sm:h-9"
                                        >
                                            Join waitlist
                                            <ArrowRight
                                                className="size-4 shrink-0"
                                                aria-hidden="true"
                                            />
                                        </Button>
                                    </form>
                                )}
                            </div>
                        </section>
                    </div>
                </div>

                <p className="pt-4 pb-2 text-sm/5 font-light text-pretty text-muted-foreground sm:text-[0.8125rem]/5">
                    Castle Labs, Inc. (dba &quot;RWA.xyz&quot;) does not offer
                    or recommend investments. Any offer is made solely by the
                    relevant issuer under its offering documents. Joining the
                    waitlist does not guarantee access; eligibility and approval
                    are determined by the issuer.
                </p>
            </div>
        </Modal>
    );
}
